import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import SiteHeader from '../components/SiteHeader';
import StatusBadge from '../components/StatusBadge';
import RequireSession from '../components/RequireSession';
import { apiErrorMessage, fetchMyOrders, type Order } from '../lib/api';
import { formatCents, formatTermDays } from '../lib/format';
import { miningModeShortLabel } from '../lib/miningModes';
import { describeOrder, isUnpaid } from '../lib/orderStatus';

/**
 * The signed-in customer's orders.
 *
 * Scope discipline: a list and a way into each order, and nothing else. No filtering, no
 * search, no renewal, no re-order, no invoices -- those belong to the Phase 2 dashboard.
 * This exists because a customer needs to find their order without hunting for the
 * reference in an email.
 *
 * The list comes from the account API, which scopes every query to the session's own
 * customer, so there is no client-side filtering to get wrong.
 */

function OrdersContent() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchMyOrders(controller.signal)
      .then(setOrders)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(apiErrorMessage(caught, 'We could not load your orders.'));
      });

    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <p role="alert" className="mm-note mm-note--danger">
        <span>{error}</span>
      </p>
    );
  }

  if (!orders) {
    return (
      <p className="mm-help" role="status">
        Loading your orders…
      </p>
    );
  }

  if (orders.length === 0) {
    return (
      <div data-testid="orders-empty">
        <p className="mm-lede">You don&rsquo;t have any orders yet.</p>
        <Link to="/" className="mm-btn-secondary mt-6 inline-flex">
          Configure your mining power
        </Link>
      </div>
    );
  }

  return (
    <ul className="m-0 grid list-none gap-3 p-0" data-testid="orders-list">
      {orders.map((order) => (
        <li key={order.orderNumber}>
          <Link
            to={`/orders/${order.orderNumber}`}
            data-testid={`order-${order.orderNumber}`}
            className="mm-panel block p-5 transition-colors hover:border-[color:var(--mm-border-strong)]"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="mm-numeric text-lg font-semibold tracking-tight">
                  {order.orderNumber}
                </p>
                {/*
                  WHAT WAS BOUGHT, IN ONE LINE: how much, for how long, in which mode.

                  The mining mode belongs here because it is a CHOICE the customer made,
                  not a detail of fulfilment -- two orders with identical hashrate and
                  term are different products, and the list would be lying by omission if
                  it showed them identically.

                  Nothing else joins it. Payout address, discount code, discount amount
                  and provisioning internals stay on the order detail page: this is a
                  find-my-order list, and every field added to it makes that harder.
                */}
                <p className="mm-help mt-1" data-testid={`summary-${order.orderNumber}`}>
                  {order.configuration.tier
                    ? order.configuration.tier.displayHashrate
                    : `${order.configuration.hashrateTh} TH/s`}{' '}
                  · {formatTermDays(order.configuration.termDays)} ·{' '}
                  {miningModeShortLabel(order.configuration.miningMode)}
                </p>
              </div>

              <div className="text-right">
                <StatusBadge status={describeOrder(order)} testId={`status-${order.orderNumber}`} />
                <p className="mm-numeric mm-help mt-2">
                  {formatCents(order.pricing.totalCents, order.pricing.currency)}
                </p>
              </div>
            </div>

            {/*
              ==================================================================
              AN UNPAID ORDER SAYS WHAT TO DO ABOUT IT.

              "Awaiting payment" is a state, not an instruction, and on its own it left a
              customer to guess whether the order was resumable or abandoned. This says it
              is resumable.

              STRUCTURALLY IT IS TEXT, NOT A CONTROL -- and that is the point. The whole
              card is already a link to the order, and a button or a second link nested
              inside an anchor is invalid HTML: it produces an interactive element that
              cannot be reached by keyboard in the order it appears, and assistive
              technology reports the nesting rather than the action. So the card keeps
              exactly ONE interactive element, and this is the label telling the customer
              where it goes.

              PAYMENT ITSELF IS NOT LAUNCHED FROM HERE. It happens on the order page, from
              the one implementation of the handoff. Duplicating that here would mean two
              places composing a checkout for the same order.
            */}
            {isUnpaid(order) && (
              <p
                className="mm-help mt-3 font-medium"
                style={{ color: 'var(--mm-warning)' }}
                data-testid={`resume-${order.orderNumber}`}
              >
                Continue payment{' '}
                <span aria-hidden="true">&rarr;</span>
              </p>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function Orders() {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14 flex-1 min-w-0">
        <h1 className="mm-display">Your orders</h1>
        <p className="mm-lede mt-4">Track the status of everything you&rsquo;ve ordered.</p>

        <div className="mt-8">
          <RequireSession>
            <OrdersContent />
          </RequireSession>
        </div>
      </main>
    </div>
  );
}
